import {Component, computed, effect, inject, Input, OnChanges, signal, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {BaseChartComponent, type EnergyType, type Interval} from './base-chart.component';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {AverageEnergyStatus} from '../../../../api/models/average-energy-status';
import {BaseChartDirective} from 'ng2-charts';
import {de} from 'date-fns/locale';
import {Chart, ChartDataset, ChartOptions} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import {rxResource} from '@angular/core/rxjs-interop';
import {DateTimeValue} from '../../../../api/models/date-time-value';
import {SunResponse} from '../../../../api/models/sun-response';
import {LocationStateService} from '../../../../common/services/location-state.service';
import {environment} from '../../../../environments/environment';
import {getAverageEnergyStatus} from './average-energy-response.helper';

@Component({
  selector: 'app-battery-origin-chart',
  templateUrl: './chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective],
})
export class BatteryOriginChartComponent extends BaseChartComponent implements OnChanges {
  @Input() override interval: Interval = 'day';
  protected override get summaryType(): EnergyType { return 'battery'; }
  private _interval = signal<Interval>('day');

  @Input() set date(value: Date) {
    this._date.set(value);
  }

  get date(): Date {
    return this._date();
  }

  private _date = signal<Date>(new Date());
  @Input() data: AverageEnergyResponse | undefined;

  private readonly locationStateService = inject(LocationStateService);

  // Weather resource for sunrise/sunset data - automatically reloads when date changes
  weatherResource = rxResource<SunResponse, any>({
    params: computed(() => ({body: {date: this._date().toISOString()}})),
    defaultValue: environment.showDefault
      ? {
          sunrise: this._date().toISOString(),
          sunset: this._date().toISOString(),
        }
      : {sunrise: undefined, sunset: undefined},
    stream: this.sunDataLoader,
  });

  lineChartOption: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...this.baseTooltipOptions,
      tooltip: {
        ...(this.baseTooltipOptions?.tooltip ?? {}),
        itemSort: (a, b) => a.datasetIndex - b.datasetIndex,
      },
      legend: {
        position: 'bottom',
        display: true,
        labels: {
          generateLabels: (chart: Chart) => {
            const root = document.documentElement;
            const productionColor = getComputedStyle(root).getPropertyValue('--color-production-yellow').trim();
            const consumptionColor = getComputedStyle(root).getPropertyValue('--color-consumption-blue').trim();
            const batteryColor = getComputedStyle(root).getPropertyValue('--color-battery-green').trim();
            const gridPositiveColor = '#000000'; // Einkauf: black
            const gridNegativeColor = '#808080'; // Verkauf: grey
            // Datasets: 0=production+, 1=grid+, 2=battery+, 3=consumption-, 4=grid-, 5=battery-
            // Legend order: positive side first, then negative side.
            return [
              {
                text: this.getLabel('production'),
                fillStyle: productionColor,
                strokeStyle: productionColor,
                datasetIndex: 0,
                hidden: chart.getDatasetMeta(0).hidden,
                index: 0,
                datasetIndices: [0],
              },
              {
                // Grid purchase (positive)
                text: 'Einkauf',
                fillStyle: gridPositiveColor,
                strokeStyle: gridPositiveColor,
                datasetIndex: 1,
                hidden: chart.getDatasetMeta(1).hidden,
                index: 1,
                datasetIndices: [1],
              },
              {
                text: `${this.getLabel('battery')} +`,
                // Outline-only legend symbol for battery
                fillStyle: 'rgba(0, 0, 0, 0)',
                strokeStyle: batteryColor,
                lineWidth: this.getCapLineBorderWidth(),
                datasetIndex: 2,
                hidden: chart.getDatasetMeta(2).hidden,
                index: 2,
                datasetIndices: [2],
              },
              {
                text: this.getLabel('consumption'),
                fillStyle: consumptionColor,
                strokeStyle: consumptionColor,
                datasetIndex: 3,
                hidden: chart.getDatasetMeta(3).hidden,
                index: 3,
                datasetIndices: [3],
              },
              {
                // Grid sale (negative)
                text: 'Verkauf',
                fillStyle: gridNegativeColor,
                strokeStyle: gridNegativeColor,
                datasetIndex: 4,
                hidden: chart.getDatasetMeta(4).hidden,
                index: 4,
                datasetIndices: [4],
              },
              {
                text: `${this.getLabel('battery')} -`,
                fillStyle: 'rgba(0, 0, 0, 0)',
                strokeStyle: batteryColor,
                lineWidth: this.getCapLineBorderWidth(),
                datasetIndex: 5,
                hidden: chart.getDatasetMeta(5).hidden,
                index: 5,
                datasetIndices: [5],
              },
            ];
          },
        },
        onClick: (e, legendItem, legend) => {
          const chart = legend.chart;
          const item = legendItem as { datasetIndices?: number[]; datasetIndex?: number };
          const indices = item.datasetIndices ?? (item.datasetIndex !== undefined ? [item.datasetIndex] : []);
          const first = indices[0];
          if (first === undefined) return;
          const meta0 = chart.getDatasetMeta(first);
          const newHidden = !meta0.hidden;
          indices.forEach((i: number) => {
            chart.getDatasetMeta(i).hidden = newHidden;
          });
          chart.update();
        },
      },
      annotation: {
        annotations: {},
      },
    },
    scales: {
      y: {
        stacked: true,
        ticks: {
          stepSize: 1, // Display every 0.50 on the y-axis
          precision: 0, // Ensures ticks are displayed with two decimal places
          callback: (value, index, ticks) => this.getBatteryOriginAxisTickLabel(value, index, ticks),
        },
        beginAtZero: true,
        min: -10,
        max: 10,
        border: {
          display: false
        }
      },
      x: {
        stacked: true,
        type: 'time',
        time: {
          unit: 'hour',
          tooltipFormat: 'HH:mm',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        border: {
          display: false
        }
      },
    },
  };

  private readonly sunriseSunsetEffect = effect(() => {
    const sun = this.weatherResource.value();
    const currentInterval = this._interval();

    if (currentInterval !== 'day') {
      this.removeAnnotations();
      return;
    }

    this.removeAnnotations();

    let updatedOptions = {...this.lineChartOption};

    if (sun?.sunrise) {
      updatedOptions = this.addVerticalLine(updatedOptions, new Date(sun.sunrise), 'Sonnenaufgang', 'rgb(128,128,128)');
    }
    if (sun?.sunset) {
      updatedOptions = this.addVerticalLine(updatedOptions, new Date(sun.sunset), 'Sonnenuntergang', 'rgb(128,128,128)');
    }

    // Subtle marker for the current time (only shown when viewing today)
    updatedOptions = this.addCurrentTimeLine(updatedOptions, this._date());

    this.lineChartOption = updatedOptions;
    this.chartOptions.set(this.lineChartOption);

    if (this.chart) {
      this.chart.update();
    }
  });

  constructor() {
    super();
    Chart.register(annotationPlugin);

    // React to location changes and update chart bounds
    effect(() => {
      const range = this.locationStateService.currentLocation()?.displayChart?.batteryOrigin;
      if (!range) {
        return;
      }

      const tickStep = this.getSymmetricAxisTickStep(
        Math.max(Math.abs(range.minkWh ?? 0), Math.abs(range.maxkWh ?? 0)),
      );
      this.lineChartOption = {
        ...this.lineChartOption,
        scales: {
          ...this.lineChartOption.scales,
          y: {
            ...(this.lineChartOption.scales?.['y'] ?? {}),
            min: range.minkWh ?? undefined,
            max: range.maxkWh ?? undefined,
            ticks: {
              ...((this.lineChartOption.scales?.['y'] as any)?.ticks ?? {}),
              stepSize: tickStep,
              precision: 0,
              callback: (value: string | number, index: number, ticks: any[]) => this.getBatteryOriginAxisTickLabel(value, index, ticks),
            },
          },
          x: this.lineChartOption.scales?.['x'],
        },
      };

      this.chartOptions.set(this.lineChartOption);
      if (this.chart) {
        this.chart.update();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('battery origin Chart received change', changes);
    if (changes['interval']) {
      this._interval.set(this.interval);
    }
    if (changes['data'] || changes['date'] || changes['interval']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    this.intializeChartData();
  }

  private getBatteryOriginAxisTickLabel(value: string | number, index: number, ticks: any[]): string {
    return this.getOriginAxisTickLabel(value, index, ticks, {top: 'Herkunft', bottom: 'Verwendung'});
  }

  private intializeChartData() {
    console.log('initialize battery origin Chart');
    if (!this.data) {
      return; // Exit if no data is available
    }

    const productionStatus = this.getStatus(getAverageEnergyStatus(this.data, 'production'));
    const completedProductionValues = this.processStatusValues(productionStatus);

    const consumptionStatus = this.getStatus(getAverageEnergyStatus(this.data, 'consumption') ?? getAverageEnergyStatus(this.data, 'consumptionPositive'));
    const completedConsumptionValues = this.processStatusValues(consumptionStatus);
    const completedProductionSurplusValues = this.calculateProductionSurplus(
      completedProductionValues,
      completedConsumptionValues,
    );
    const completedConsumptionUndersupplyValues = this.calculateConsumptionUndersupply(
      completedConsumptionValues,
      completedProductionValues,
    );

    const gridPositiveStatus = this.getStatus(getAverageEnergyStatus(this.data, 'gridPositive'));
    const completedGridPositiveValues = this.processStatusValues(gridPositiveStatus);

    const gridNegativeStatus = this.getStatus(getAverageEnergyStatus(this.data, 'gridNegative'));
    const completedGridNegativeValues = this.processStatusValues(gridNegativeStatus);

    const batteryPositiveStatus = this.getStatus(getAverageEnergyStatus(this.data, 'batteryPowerPositive'));
    const batteryNegativeStatus = this.getStatus(getAverageEnergyStatus(this.data, 'batteryPowerNegative'));
    const {
      batteryPositive: completedBatteryPositiveValues,
      batteryNegative: completedBatteryNegativeValues,
    } = this.processCombinedBatteryStatusValues(batteryPositiveStatus, batteryNegativeStatus);

    // POSITIVE SIDE:
    // Stack: production surplus (production - consumption) + gridPositive
    // Cap:   batteryPositive
    const positiveCapped = this.capPositiveToBattery(
      completedProductionSurplusValues,
      completedGridPositiveValues,
      completedBatteryPositiveValues,
    );

    // Distinct legend labels for positive stack.
    const productionPositiveDataset = this.createDataset(
      this.getLabel('production'),
      positiveCapped.production,
      'production',
      true,
    );
    const gridPositiveDataset = this.createDataset(
      this.getLabel('gridPositive'),
      positiveCapped.gridPositive,
      'gridPositive',
      true,
    );
    // Override colors for grid positive: black (Einkauf)
    const gridPositiveColor = '#000000';
    gridPositiveDataset.borderColor = gridPositiveColor;
    gridPositiveDataset.backgroundColor = gridPositiveColor;
    const batteryPositiveDataset = this.createDataset(
      `${this.getLabel('battery')} +`,
      completedBatteryPositiveValues,
      'battery',
      false,
    );

    // NEGATIVE SIDE:
    // Stack: undersupply (consumption - production, shown as negative) + gridNegative
    // Cap:   batteryNegative
    const negativeCapped = this.capNegativeToBattery(
      completedConsumptionUndersupplyValues,
      completedGridNegativeValues,
      completedBatteryNegativeValues,
    );

    const consumptionNegativeDataset = this.createDataset(
      this.getLabel('consumption'),
      negativeCapped.consumption,
      'consumption',
      true,
    );
    const gridNegativeDataset = this.createDataset(
      this.getLabel('gridNegative'),
      negativeCapped.gridNegative,
      'gridNegative',
      true,
    );
    // Override colors for grid negative: grey (Verkauf)
    const gridNegativeColor = '#808080';
    gridNegativeDataset.borderColor = gridNegativeColor;
    gridNegativeDataset.backgroundColor = gridNegativeColor;
    const batteryNegativeDataset = this.createDataset(
      `${this.getLabel('battery')} -`,
      negativeCapped.batteryNegative,
      'battery',
      false,
    );


    // Add labels only once (based on production values)
    const labels: Date[] = this.populateLabels(completedProductionValues);

    const datasets = [];
    // Order matters for the custom plugin (first 3 datasets).
    // Positive stack (above zero).
    datasets.push(productionPositiveDataset);
    datasets.push(gridPositiveDataset);
    datasets.push(batteryPositiveDataset);
    // Negative stack (below zero).
    datasets.push(consumptionNegativeDataset);
    datasets.push(gridNegativeDataset);
    datasets.push(batteryNegativeDataset);

    // Update chart data
    this.updateChart(labels, datasets);
  }

  /**
   * Extracts the status object or provides a default.
   */
  private getStatus(status: AverageEnergyStatus | undefined): AverageEnergyStatus {
    return status ?? {deviceNames: {}, deviceTimeValues: {}, timeBucket: ''};
  }

  /**
   * Processes device time values by summarizing and completing them.
   */
  private processStatusValues(status: AverageEnergyStatus): DateTimeValue[] {
    const summarizedValues = super.summarizeByTimestamp(Object.values(status.deviceTimeValues || {}));
    return super.completeData(summarizedValues, 'day', this.date, 'zero', status.timeBucket);
  }

  /**
   * Summarize battery values across both battery power buckets first, then split by sign.
   * 0 is treated as positive so only one side is active per timestamp.
   */
  private processCombinedBatteryStatusValues(
    batteryPositiveStatus: AverageEnergyStatus,
    batteryNegativeStatus: AverageEnergyStatus,
  ): { batteryPositive: DateTimeValue[]; batteryNegative: DateTimeValue[] } {
    const summarizedBatteryValues = super.summarizeByTimestamp([
      ...Object.values(batteryPositiveStatus.deviceTimeValues || {}),
      ...Object.values(batteryNegativeStatus.deviceTimeValues || {}),
    ]);
    const completedBatteryValues = super.completeData(
      summarizedBatteryValues,
      'day',
      this.date,
      'zero',
      batteryPositiveStatus.timeBucket || batteryNegativeStatus.timeBucket,
    );

    return {
      batteryPositive: completedBatteryValues.map((v) => {
        const netValue = v.value ?? 0;
        return {
          ...v,
          value: netValue >= 0 ? netValue : 0,
        };
      }),
      batteryNegative: completedBatteryValues.map((v) => {
        const netValue = v.value ?? 0;
        return {
          ...v,
          value: netValue < 0 ? netValue : 0,
        };
      }),
    };
  }

  /**
   * Compute production surplus per timestamp:
   * surplus = max(production - consumption, 0)
   */
  private calculateProductionSurplus(
    productionValues: DateTimeValue[],
    consumptionValues: DateTimeValue[],
  ): DateTimeValue[] {
    const consumptionByRead = new Map<string, number>();
    consumptionValues.forEach((v) => {
      if (!v.read) return;
      consumptionByRead.set(v.read, Math.max(0, v.value ?? 0));
    });

    return productionValues.map((v) => {
      const production = Math.max(0, v.value ?? 0);
      const consumption = v.read ? (consumptionByRead.get(v.read) ?? 0) : 0;
      return {
        ...v,
        value: Math.max(0, production - consumption),
      };
    });
  }

  /**
   * Compute consumption undersupply per timestamp:
   * undersupply = max(consumption - production, 0)
   */
  private calculateConsumptionUndersupply(
    consumptionValues: DateTimeValue[],
    productionValues: DateTimeValue[],
  ): DateTimeValue[] {
    const productionByRead = new Map<string, number>();
    productionValues.forEach((v) => {
      if (!v.read) return;
      productionByRead.set(v.read, Math.max(0, v.value ?? 0));
    });

    return consumptionValues.map((v) => {
      const consumption = Math.max(0, v.value ?? 0);
      const production = v.read ? (productionByRead.get(v.read) ?? 0) : 0;
      return {
        ...v,
        value: Math.max(0, consumption - production),
      };
    });
  }

  /**
   * POSITIVE SIDE CAP (Battery as cap):
   * Caps (positive) production + (positive) grid per point
   * so the stacked sum never exceeds (positive) battery power,
   * using PRIORITY allocation:
   *  - production uses as much of the cap as it can (up to its full value)
   *  - grid uses only whatever remaining cap is left.
   */
  private capPositiveToBattery(
    production: DateTimeValue[],
    gridPositive: DateTimeValue[],
    batteryPositive: DateTimeValue[],
  ): { production: DateTimeValue[]; gridPositive: DateTimeValue[] } {
    // Work on copies to avoid unexpected side-effects and clamp to >= 0.
    const p = production.map((v) => ({ ...v, value: Math.max(0, v.value ?? 0) }));
    const gPos = gridPositive.map((v) => ({ ...v, value: Math.max(0, v.value ?? 0) }));
    const bPos = batteryPositive.map((v) => ({ ...v, value: Math.max(0, v.value ?? 0) }));

    const idxByRead = (arr: DateTimeValue[]) => {
      const m = new Map<string, number>();
      arr.forEach((v, i) => {
        if (v.read) m.set(v.read, i);
      });
      return m;
    };

    const pIdx = idxByRead(p);
    const gIdx = idxByRead(gPos);
    const bIdx = idxByRead(bPos);

    // Build the union of timestamps from production and grid so we can
    // also apply a cap of 0 where there is **no** battery value.
    const allReads = new Set<string>();
    p.forEach((v) => v.read && allReads.add(v.read));
    gPos.forEach((v) => v.read && allReads.add(v.read));

    allReads.forEach((read) => {
      const pi = pIdx.get(read);
      const gi = gIdx.get(read);
      const bi = bIdx.get(read);

      const batteryCap = bi !== undefined ? Math.max(0, bPos[bi].value ?? 0) : 0;
      const pv = pi !== undefined ? (p[pi].value ?? 0) : 0;
      const gv = gi !== undefined ? (gPos[gi].value ?? 0) : 0;

      if (batteryCap <= 0) {
        // Treat missing or non-positive battery as cap 0 -> zero both series.
        if (pi !== undefined) p[pi].value = 0;
        if (gi !== undefined) gPos[gi].value = 0;
        return;
      }

      if (pv <= 0 && gv <= 0) {
        return;
      }

      // Allocate cap to production first.
      const allocatedProduction = Math.min(pv, batteryCap);
      const remainingCap = Math.max(0, batteryCap - allocatedProduction);

      // Grid gets only the remaining cap.
      const allocatedGrid = Math.min(gv, remainingCap);

      if (pi !== undefined) p[pi].value = allocatedProduction;
      if (gi !== undefined) gPos[gi].value = allocatedGrid;
    });

    return { production: p, gridPositive: gPos };
  }

  /**
   * NEGATIVE SIDE CAP (Battery as cap):
   * Caps (positive) consumption + (negative) grid per point
   * so the stacked negative sum never exceeds (negative) battery power,
   * using PRIORITY allocation (consumption first, then grid) instead of
   * proportional scaling.
   *
   * Inputs:
   *  - consumptionPositive: positive values (kWh)
   *  - gridNegative: negative values
   *  - batteryNegative: negative values (discharge from battery)
   *
   * Output values are NEGATIVE (for rendering below zero).
   */
  private capNegativeToBattery(
    consumptionPositive: DateTimeValue[],
    gridNegative: DateTimeValue[],
    batteryNegative: DateTimeValue[],
  ): {
    consumption: DateTimeValue[];
    gridNegative: DateTimeValue[];
    batteryNegative: DateTimeValue[];
  } {
    // Work internally with magnitudes (all >= 0).
    const consMag = consumptionPositive.map((v) => ({
      ...v,
      value: Math.max(0, v.value ?? 0),
    }));
    const gridMag = gridNegative.map((v) => ({
      ...v,
      value: Math.max(0, Math.abs(v.value ?? 0)),
    }));
    const battMag = batteryNegative.map((v) => ({
      ...v,
      value: Math.max(0, Math.abs(v.value ?? 0)),
    }));

    const idxByRead = (arr: DateTimeValue[]) => {
      const m = new Map<string, number>();
      arr.forEach((v, i) => {
        if (v.read) m.set(v.read, i);
      });
      return m;
    };

    const cIdx = idxByRead(consMag);
    const gIdx = idxByRead(gridMag);
    const bIdx = idxByRead(battMag);

    // Build the union of timestamps so we also cap to 0
    // where there is **no** batteryNegative value.
    const allReads = new Set<string>();
    consMag.forEach((v) => v.read && allReads.add(v.read));
    gridMag.forEach((v) => v.read && allReads.add(v.read));

    allReads.forEach((read) => {
      const ci = cIdx.get(read);
      const gi = gIdx.get(read);
      const bi = bIdx.get(read);

      const batteryCap = bi !== undefined ? Math.max(0, battMag[bi].value ?? 0) : 0;
      const cv = ci !== undefined ? (consMag[ci].value ?? 0) : 0;
      const gv = gi !== undefined ? (gridMag[gi].value ?? 0) : 0;
      const total = cv + gv;

      if (batteryCap <= 0) {
        // Treat missing or non-positive battery as cap 0 -> zero both series.
        if (ci !== undefined) consMag[ci].value = 0;
        if (gi !== undefined) gridMag[gi].value = 0;
        return;
      }

      if (cv <= 0 && gv <= 0) {
        return;
      }

      if (total <= batteryCap) {
        // No capping needed when sum is within cap.
        return;
      }

      // Allocate cap to consumption first.
      const allocatedConsumption = Math.min(cv, batteryCap);
      const remainingCap = Math.max(0, batteryCap - allocatedConsumption);

      // Grid gets only the remaining cap.
      const allocatedGrid = Math.min(gv, remainingCap);

      if (ci !== undefined) consMag[ci].value = allocatedConsumption;
      if (gi !== undefined) gridMag[gi].value = allocatedGrid;
    });

    // Convert magnitudes back to negative values for rendering below zero.
    const consumptionOut = consMag.map((v) => ({ ...v, value: -(v.value ?? 0) }));
    const gridNegativeOut = gridMag.map((v) => ({ ...v, value: -(v.value ?? 0) }));
    const batteryNegativeOut = battMag.map((v) => ({ ...v, value: -(v.value ?? 0) }));

    return {
      consumption: consumptionOut,
      gridNegative: gridNegativeOut,
      batteryNegative: batteryNegativeOut,
    };
  }



  /**
   * Populates labels based on processed values.
   */
  private populateLabels(values: DateTimeValue[]): Date[] {
    const labels: Date[] = [];
    values.forEach((item) => {
      const date = new Date(item.read ?? '');
      if (!isNaN(date.getTime())) {
        labels.push(date);
      }
    });
    return labels;
  }

  /**
   * Creates a dataset for the chart.
   */
  private createDataset(label: string, values: DateTimeValue[], colorKey: string, stacked: boolean): ChartDataset {
    const color = this.getColor(colorKey);
    const isCapLine = !stacked;
    const dataset: ChartDataset & {isCapLine?: boolean} = {
      // `label` is already the final, localized legend text (may include +/- suffix).
      label,
      data: values.map((v) => v.value!),
      borderColor: color,
      backgroundColor: stacked ? color : 'transparent',
      // Battery datasets are created with stacked = false, so they will not be filled.
      fill: stacked ? true : false,
      pointRadius: 0, // Hide dots for better readability
      pointHoverRadius: 0,
      tension: 0.1, // Smooth interpolation between points
      cubicInterpolationMode: 'default',
      stack: stacked ? 'stack1' : undefined,
      borderWidth: isCapLine ? this.getCapLineBorderWidth() : this.isMobile() ? 1 : 2,
      isCapLine: isCapLine,
      order: isCapLine ? 99 : 0,
    };
    return dataset;
  }

  /**
   * Updates the chart with the provided labels and datasets.
   */
  private updateChart(labels: Date[], datasets: Record<string, any>[]): void {
    this.chartType.set('line');
    // Re-apply the current-time marker on every rebuild so it survives view switches
    if (this._interval() === 'day') {
      this.lineChartOption = this.addCurrentTimeLine(this.lineChartOption, this._date());
    }
    this.chartOptions.set(this.lineChartOption);
    this.chartData.set({
      labels: labels,
      datasets: datasets,
    });
    if (this.chart) {
      this.chart.update();
    }
  }

  private removeAnnotations() {
    if (this.lineChartOption.plugins?.annotation) {
      this.lineChartOption.plugins.annotation.annotations = {};
      this.lineChartOption = {...this.lineChartOption};
    }

    if (this.chart?.options?.plugins?.annotation) {
      this.chart.options.plugins.annotation.annotations = {};
    }

    this.chartOptions.set(this.lineChartOption);

    if (this.chart) {
      this.chart.update();
    }
  }

  plugin = {
    id: 'cutterPlugin',
    afterDatasetsDraw(chart: any) {
      const {
        ctx,
        chartArea: {top, bottom},
        scales: {x, y},
      } = chart;
      const baseDataset = chart.data.datasets[0];
      const topDataset = chart.data.datasets[1];
      const cutDataset = chart.data.datasets[2];

      chart.data.labels.forEach((label: any) => {
        const i = chart.data.labels.indexOf(label);
        const base = baseDataset.data[i];
        const topVal = topDataset.data[i];
        const stackTotal = base + topVal;
        const cutVal = cutDataset.data[i];

        if (cutVal >= stackTotal) {
          // draw nothing — entire stack is under the cutter
          return;
        }

        if (cutVal <= 0) return; // nothing to cut

        const barWidth = x.getPixelForTick(i + 1) - x.getPixelForTick(i);
        const centerX = x.getPixelForValue(i);
        const barLeft = centerX - barWidth / 4;
        const barRight = centerX + barWidth / 4;

        const yCut = y.getPixelForValue(cutVal);
        const yTop = y.getPixelForValue(stackTotal);

        ctx.save();
        // Use a fully transparent fill so we no longer paint visible white areas on the chart.
        ctx.fillStyle = 'rgba(0, 0, 0, 0)';
        ctx.fillRect(barLeft, yTop, barRight - barLeft, yCut - yTop);
        ctx.restore();
      });
    },
  };
}
